import styled from '@emotion/styled';
import { useEffect, useRef, useState } from 'react';

import { ToastManager } from '../../../../ToastManager';
import { SolidButton } from '../../../buttons/SolidButton';
import { Glyph } from '../../../common/Glyph';
import { Image } from '../../../common/Image';
import { SpinnerFade } from '../../../common/SpinnerFade';
import { DeviceTile } from '../../../device/DeviceTile';

import { useEventCallback } from '../../../../hooks/useEventCallback.ts';
import { useI18n } from '../../../../hooks/useI18nFormatters';
import { useDriver } from '../../../../store/drivers/useDrivers.ts';
import { useApi } from '../../../../store/SelectedHomeyStore.hooks.ts';

import { animationFade } from '../../../../theme/animations/animationFade';
import { publicUrl } from '../../../../theme/functions/publicUrl.js';
import { su } from '../../../../theme/functions/su';
import { glyph } from '../../../../theme/glyph';
import { motion } from '../../../../theme/motion';
import { theme } from '../../../../theme/theme';
import glyphStyles from '../../../common/Glyph.module.css';

export function ConnectView(props) {
  const { i18n } = useI18n();

  const instanceRef = useRef({});
  instanceRef.current.onConnectPress = props.onConnectPress;

  const { api } = useApi();
  const { driver } = useDriver({ driverId: props.driverId });
  const [availability, setAvailability] = useState({
    zwave: null,
    ble: null,
    zigbee: null,
    infrared: null,
    rf433: null,
    hasAvailabilitySupport: false,
    isLoading: true,
    error: null,
  });

  const fetchAvailability = useEventCallback(async () => {
    const [zwave, ble, zigbee, rf] = await Promise.all([
      api.zwave.getState(),
      api.ble.getState(),
      api.zigbee.getState(),
      api.rf.getState?.() ?? Promise.resolve({}),
    ]);

    const hasAvailabilitySupport =
      zwave.availability != null ||
      ble.availability != null ||
      zigbee.availability != null ||
      rf.availability != null;

    setAvailability({
      zwave: zwave.availability?.zwave ?? null,
      ble: ble.availability?.ble ?? null,
      zigbee: zigbee.availability?.zigbee ?? null,
      infrared: rf.availability?.infrared ?? null,
      rf433: rf.availability?.rf433 ?? null,
      hasAvailabilitySupport,
      isLoading: false,
      error: null,
    });
  });

  useEffect(() => {
    fetchAvailability().catch((error) => {
      setAvailability({
        zwave: null,
        ble: null,
        zigbee: null,
        infrared: null,
        rf433: null,
        hasAvailabilitySupport: false,
        isLoading: false,
        error: error,
      });
      ToastManager.handleError(error);
    });
  }, [fetchAvailability]);

  const [isInstalling, setIsInstalling] = useState(false);

  useEffect(() => {
    // when it was not installed before but is installed now
    if (driver != null && driver.ready === true && props.isInstalled === false) {
      instanceRef.current.onConnectPress(driver.connectivity, availability);
    }
  }, [driver, availability, props.isInstalled]);

  function getButtonText() {
    if (isInstalling) return i18n.messageFormatter('pair.installingApp');
    if (props.isInstalled) return i18n.messageFormatter('common.connect');
    return i18n.messageFormatter('pair.installAndConnect');
  }

  async function handleConnectPress() {
    if (isInstalling) return;

    if (props.isInstalled === false) {
      setIsInstalling(true);

      try {
        // handled further in useEffect
        await api.apps.installFromAppStore({
          id: props.appInfo.appId,
        });
      } catch (error) {
        ToastManager.handleError(error);
        setIsInstalling(false);
      }

      return;
    }

    props.onConnectPress(driver.connectivity, availability);
  }

  return (
    <ConnectView.Root>
      <ConnectView.Info>
        <ConnectView.IconWrapper backgroundColor={props.appInfo.appColor}>
          {props.appInfo.appId === 'vdevice' ? (
            <Image url={publicUrl('img/logo192.png')} size="100px" />
          ) : (
            <Glyph source={props.appInfo.appIcon} color={theme.color.white} size="50px" />
          )}
        </ConnectView.IconWrapper>

        <ConnectView.AppName>{props.appInfo.appName}</ConnectView.AppName>

        <ConnectView.Message>
          {i18n.messageFormatter('pair.connect', {
            name: props.driverInfo.driverName,
          })}
        </ConnectView.Message>

        <DeviceTile
          isNoInteraction
          iconUrl={props.driverInfo.driverIcon}
          name={props.driverInfo.driverName}
        />
      </ConnectView.Info>

      {props.appInfo.appPermissions.includes('homey:manager:api') && (
        <ConnectView.WarningWrapper>
          <ConnectView.Warning>
            <Glyph
              source={glyph.core.exclamationmarkCircleFill}
              color={theme.color.white}
              size={theme.icon.size_xlarge}
            />
            {i18n.messageFormatter('pair.permissionAPI')}
          </ConnectView.Warning>
        </ConnectView.WarningWrapper>
      )}

      <ConnectView.Footer>
        <SolidButton
          styleColor="green"
          styleFlat={true}
          styleWidth="full"
          isDisabled={isInstalling === true || availability.isLoading === true}
          onPress={handleConnectPress}
        >
          <SpinnerFade
            isActive={isInstalling === true || availability.isLoading === true}
            color={theme.color.white}
            size={theme.icon.size_small}
          />
          <SolidButton.Text>{getButtonText()}</SolidButton.Text>
        </SolidButton>
      </ConnectView.Footer>
    </ConnectView.Root>
  );
}

ConnectView.Root = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: ${su(2)};

  width: 100%;
  overflow-y: auto;
  flex: 1 1 auto;
  animation: ${animationFade.in} ${motion.duration.slow} ease-out 1 forwards;
`;

ConnectView.Info = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1 1 auto;
  margin: ${su(2, 0)};
`;

ConnectView.IconWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 100px;
  width: 100px;
  border-radius: 50%;
  background-color: ${(props) => props.backgroundColor ?? 'transparent'};
`;

ConnectView.AppName = styled.h2`
  padding: ${su(2, 0, 1)};
  text-align: center;
`;

ConnectView.Message = styled.div`
  padding-bottom: ${su(4)};
  color: ${theme.color.text_light};
  text-align: center;
`;

ConnectView.WarningWrapper = styled.div`
  padding: ${su(1, 0)};
`;

ConnectView.Warning = styled.div`
  display: flex;
  align-items: center;
  background-color: ${theme.color.warning};
  color: ${theme.color.white};
  border-radius: ${theme.borderRadius.default};
  padding: ${su(2, 1)};

  .${glyphStyles.root} {
    margin-inline-end: ${su(1)};
  }
`;

ConnectView.Footer = styled.div`
  display: flex;
  width: 100%;
`;
